import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Notice } from '../../../types';
import { noticesCache } from '../../../utils/notices-cache';
import { NoticesService } from '../services/noticeService';

type UseNoticesReturn = {
  notices: Notice[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
};

/**
 * Custom hook for managing notices data
 * Uses centralized cache to prevent multiple API calls
 */
export const useNotices = (): UseNoticesReturn => {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Use ref to maintain service instance across renders
  const serviceRef = useRef<NoticesService | null>(null);

  // Initialize service only once
  if (!serviceRef.current) {
    serviceRef.current = new NoticesService();
  }

  // Memoize the service to prevent re-creation
  const service = useMemo(() => serviceRef.current!, []);

  // Update local state from cache
  const updateFromCache = useCallback(() => {
    const cachedData = noticesCache.get();
    const status = noticesCache.getStatus();

    if (cachedData) {
      setNotices(cachedData);
    }

    setIsLoading(status.isLoading);
    setError(status.error);
  }, []);

  const fetchNotices = useCallback(
    async (force = false) => {
      // Check cache first (unless forced)
      if (!force) {
        const cachedData = noticesCache.get();
        if (cachedData) {
          setNotices(cachedData);
          return;
        }
      }

      // Prevent multiple simultaneous requests
      if (noticesCache.isLoading()) {
        return;
      }

      try {
        noticesCache.setLoading(true);
        setIsLoading(true);
        setError(null);

        const response = await service.getActiveNotices();

        if (response instanceof Error) {
          throw response;
        }

        // Update cache and local state
        noticesCache.set(response);
        setNotices(response);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Error al cargar los avisos';
        noticesCache.setError(errorMessage);
        setError(errorMessage);
        console.error('Error fetching notices:', err);
      } finally {
        setIsLoading(false);
      }
    },
    [service]
  );

  const refetch = useCallback(() => {
    fetchNotices(true);
  }, [fetchNotices]);

  // Subscribe to cache changes
  useEffect(() => {
    const unsubscribe = noticesCache.subscribe(updateFromCache);

    // Initial load
    updateFromCache();

    // If no cached data, fetch from API
    if (!noticesCache.get() && !noticesCache.isLoading()) {
      fetchNotices();
    }

    return unsubscribe;
  }, [updateFromCache, fetchNotices]);

  return {
    notices,
    isLoading,
    error,
    refetch
  };
};
